<script setup lang="ts">
import { api } from '@/api';
import {
  DefinitionDto,
  DefinitionParameterDto,
  NumericQuantityDto,
  ProtocolDto,
  UnitDto,
} from '@/api/generator';
import { getDefaultOptions } from '@/components/confirmationModals/defaults';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import Select from '@/components/forms/Select/Select.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { SelectItemTypeObject } from '@/components/forms/types';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { useProtocolsStore } from '@/stores/protocolsStore';
import { useUnitsStore } from '@/stores/unitsStore';
import { idEqual } from '@/utils/compareAndSort';
import { mdiPlus } from '@mdi/js';
import { useTranslation } from 'i18next-vue';
import { NIL, v7 as uuidv7 } from 'uuid';
import { useFieldArray, useFormValues, useSetFieldValue } from 'vee-validate';
import { computed, nextTick, onMounted, ref } from 'vue';
import { toCreateDefinition } from '../mappings';
import { valueTypeOptions } from './quantityFormHelpers';
import DefinitionParameterForm from './useInEvaluations/DefinitionParameterForm.vue';
import { isPredefinedEntity } from '@/utils/helper';
import { backendValidator } from '@/validation/validators/backendValidator';
import Button from '@/components/buttons/Button.vue';

type Quantity = NumericQuantityDto;
interface AddDefinitionModalFormProps {
  value?: DefinitionDto;
  allowExtend: boolean;
}

const { t } = useTranslation();

const confirmationOptions = getDefaultOptions(t);

const props = defineProps<AddDefinitionModalFormProps>();

const unitsStore = useUnitsStore();
const protocolsStore = useProtocolsStore();
const { value: formValues } = useFormValues<Quantity>();

const inEditMode = computed(() => !!props.value && props.value?.id !== NIL);

const hasDependingParameterSets = computed(() => {
  return Boolean(props.value?.definitionParameterValueSets?.length);
});
const disabled = computed(
  () =>
    (hasDependingParameterSets.value && inEditMode.value) ||
    (inEditMode.value && (formValues.isReferenced || isPredefinedEntity(formValues))),
);

const { form } = useVeeForm<DefinitionDto>({
  schemaName: 'DefinitionDto',
  mode: 'add',

  options: {
    keepValuesOnUnmount: true, // keep values when parent form switches between tabs
    initialValues: {
      ...(props.value || {
        id: NIL,
        definitionParameters: [],
        definitionParameterValueSets: [],
        valueType: 'FloatingPoint',
      }),
    },
  },

  customValidateFunctions: [
    backendValidator((definitionDto, errorHandlers) =>
      api.numericQuantitiesApi.validateDefinition(
        {
          createDefinitionDto: toCreateDefinition(definitionDto),
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, isSubmitting, values } = form;

const { fields, push, remove } = useFieldArray<DefinitionParameterDto>('definitionParameters');
const selectedUnit = ref<UnitDto | undefined>(undefined);
const selectUnitId = useSetFieldValue<string | undefined>('unitId');

function updateSelectedUnit(unit: UnitDto | undefined): void {
  selectedUnit.value = unit;
  selectUnitId(unit?.id);
}

const selectedProtocol = ref<ProtocolDto | undefined>(undefined);
const selectProtocolId = useSetFieldValue<string>('protocolId');
function updateSelectedProtocol(protocol: ProtocolDto | undefined): void {
  selectedProtocol.value = protocol;
  if (protocol?.id) {
    selectProtocolId(protocol.id);
  }
}

const emits = defineEmits<{
  update: [DefinitionDto];
  close: [];
}>();

onMounted(async () => {
  await unitsStore.initialize();
  await protocolsStore.initialize();

  if (values?.protocolId) {
    selectedProtocol.value = protocolsStore.getById(values.protocolId);
  }
  if (values?.unitId) {
    selectedUnit.value = unitsStore.getById(values.unitId);
  }
});

const { action: cancel } = useActionWithConfirmation({
  confirmedAction: () => {
    emits('close');
  },
  confirmationOptions,
});

const submit = handleSubmit(async (definition: DefinitionDto) => {
  emits('update', definition);
});

function addParameter() {
  const newParameter: Partial<DefinitionParameterDto> = {
    id: NIL,
    parameterIdentifier: uuidv7(),
    decimalPlaces: 3,
    valueType: 'FloatingPoint',
  };

  push(newParameter as DefinitionParameterDto);

  focusLastParameter();
}

function focusLastParameter() {
  nextTick(() => {
    const inputElement = document.querySelector(
      `input[name="definitionParameters[${fields.value.length - 1}].name"]`,
    ) as HTMLElement;
    inputElement?.focus();
  });
}

const unitOptions = computed<SelectItemTypeObject<UnitDto | null>[]>(() => [...unitsStore.options]);

const onCancel = (e?: Event) => {
  if (form.meta.value.dirty) return cancel({ param: e });
  emits('close');
};
</script>

<template>
  <v-row>
    <v-col cols="6">
      <VeeTextField :label="$t('Name')" name="name" :placeholder="$t('Required')" class="mb-6" />
    </v-col>
    <v-col cols="4">
      <VeeSelect
        name="valueType"
        :label="$t('ValueType')"
        class="mb-6 wide-select"
        :items="valueTypeOptions"
        :disabled="disabled"
      />
    </v-col>

    <v-col cols="2">
      <Select
        class="mb-6 w-100"
        emptyAsUndefined
        :label="$t('DefinitionUnit_one')"
        :clearable="true"
        :modelValue="selectedUnit"
        :items="unitOptions"
        :valueComparator="idEqual"
        :disabled="disabled"
        @update:modelValue="updateSelectedUnit"
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col cols="6" calss="pb-0">
      <VeeTextarea :label="$t('Description')" name="description" />
    </v-col>
    <v-col cols="6" class="pb-0 mb-0">
      <Select
        :clearable="true"
        :modelValue="selectedProtocol"
        :label="$t('DefinitionProtocol_one')"
        :items="protocolsStore.options"
        @update:modelValue="updateSelectedProtocol"
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col cols="6" class="pt-0">
      <VeeAttachments class="mb-3" name="attachments" data-testid="Description_Attachments" />
    </v-col>
  </v-row>

  <VDivider
    v-if="
      !hasDependingParameterSets &&
      form.values.definitionParameters &&
      form.values.definitionParameters?.length > 0
    "
    class="mb-10 mt-3"
  />

  <v-row v-if="!hasDependingParameterSets">
    <v-col cols="6">
      <template
        v-if="form.values.definitionParameters && form.values.definitionParameters?.length > 0"
      >
        <h4>
          {{ $t('DefinitionParameter_other') }}
        </h4>
      </template>
    </v-col>
    <v-col cols="6" class="d-flex justify-end">
      <Button
        variant="ghost"
        :icon="mdiPlus"
        iconPosition="start"
        :label="$t('AddDefinitionParameter')"
        size="x-small"
        class="mb-12"
        :disabled="disabled"
        @click="addParameter"
      />
    </v-col>
  </v-row>

  <ul v-if="!hasDependingParameterSets">
    <DefinitionParameterForm
      v-for="(item, index) in fields"
      :key="`${item.key}`"
      :name="`definitionParameters[${index}]`"
      :allowExtend="hasDependingParameterSets && allowExtend && inEditMode"
      @remove="remove(index)"
    />
  </ul>

  <div class="d-flex justify-end mb-5">
    <Button variant="text" class="mr-6" @click="onCancel">
      {{ $t('Cancel') }}
    </Button>

    <Button variant="primary" :loading="isSubmitting" @click="submit">
      {{ value ? $t('Apply') : $t('AddDefinition') }}
    </Button>
  </div>
</template>

<style scoped lang="scss">
ul {
  list-style: none;
}

.remove-btn {
  position: absolute;
  right: 10px;
  bottom: 10px;
}
</style>
